import { format, isValid, parseISO } from 'date-fns'

import type { TStopAppointment } from '../../types/appointment'

interface AppointmentProps {
  appointment: TStopAppointment
  fontSize?: string
}

const fontSizeClasses: Record<string, string> = {
  lg: 'text-lg',
  md: 'text-base',
  sm: 'text-sm'
}

/**
 * Displays the appointment date/time of the stop
 */
export const Appointment = (props: AppointmentProps) => {
  const { appointment, fontSize = 'sm' } = props
  const { start, end } = appointment

  const textColor = 'text-gray-600 dark:text-gray-400'

  // Format: "Fri, Oct 31, 8:00 AM"
  const dateTimeFormat = 'EEE, MMM d, h:mm a'

  // Helper function to format date and time together
  const formatAppointmentDateTime = (date?: string, time?: string): string | null => {
    if (!date) return null

    try {
      // Parse the date
      let dateObj = parseISO(date)

      // If date parsing fails, try creating a new Date
      if (!isValid(dateObj)) {
        dateObj = new Date(date)
      }

      if (!isValid(dateObj)) {
        return null
      }

      // If time is provided, set the hours and minutes
      if (time) {
        const [hours, minutes] = time.split(':').map(Number)
        if (!isNaN(hours) && !isNaN(minutes)) {
          dateObj.setHours(hours, minutes, 0, 0)
        }
      }

      return format(dateObj, dateTimeFormat)
    } catch (error) {
      console.error('Error formatting appointment date/time:', error)
      return null
    }
  }

  // Format start date/time
  const startDateTime = formatAppointmentDateTime(start?.date, start?.time)
  // Format end date/time
  const endDateTime = formatAppointmentDateTime(end?.date, end?.time)

  // If we have both start and end, and they're different, show range
  if (startDateTime && endDateTime && startDateTime !== endDateTime) {
    return (
      <div className="flex flex-col">
        <span className={`${textColor} ${fontSizeClasses[fontSize]}`}>{startDateTime}</span>
        <span className={`${textColor} ${fontSizeClasses[fontSize]}`}>{endDateTime}</span>
      </div>
    )
  }

  // Otherwise, just show the start (or end if no start)
  const displayDateTime = startDateTime || endDateTime

  if (!displayDateTime) {
    return null
  }

  return <span className={`${textColor} ${fontSizeClasses[fontSize]}`}>{displayDateTime}</span>
}
